import { faArrowRotateLeft, faFilter } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Skeleton, Stack } from '@mui/material'
import { getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import AbiStatementViewApiUrls from 'src/apiUrls/AbiStatementViewApiUrls'
import { createDefaultValues } from 'src/components/era-package/forms/hooks/useFormConfig'
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection'
import BlankCard from 'src/components/era-package/shared/BlankCard'
import { Table } from 'src/components/era-package/Table'
import { dateRangeFilter, globalFilterFn } from 'src/utils/era-package/filters'
import { StatementDialog } from 'src/views/abi/StatementPage/dialogs'
import { dailyStatementCols, dailyStatementFilters, optionsMap, STATEMENT_TYPE } from 'src/views/abi/StatementPage/shared'

// INTEGRATION: Fetch data from backend
const fetchData = (setData = () => { }, setLoading = () => { }) => {
  let isAlive = true
  setLoading(true)
  fetch(AbiStatementViewApiUrls.GET_DAILY, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
      })
    .then(res => res.json())
    .then(async (result) => {
      if (isAlive && result.status === 200) setData(result.data)
    }).catch(() => {
      if (isAlive) setData([])
    }).finally(() => {
      if (isAlive) setLoading(false)
    })

  return () => { isAlive = false }
}
// END INTEGRATION

const DIALOG_PROPS_RESET = {
  open: false, data: {},
  type: STATEMENT_TYPE.daily
}

const DEFAULT_FILTERS = createDefaultValues(dailyStatementFilters)

const RESET_FILTERS = {
  ...DEFAULT_FILTERS,
  isFinalized: ''
};

// Exclude these fields from being auto-mapped onto the table column filter function.
const EXCLUDE_FILTERS = ['global', 'entryNumber']

export const DailyTab = () => {
  const filtersForm = useMemo(() => ({
    fields: dailyStatementFilters,
    title: 'Filters',
    icon: <FontAwesomeIcon icon={faFilter} />,
  }), [])
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [dialogProps, setDialogProps] = useState(DIALOG_PROPS_RESET)

  const table = useReactTable({
    data, columns: dailyStatementCols,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: row => row.original.relatedEntrySummary?.length > 0,
    getSubRows: row => row.relatedEntrySummary,
    getRowId: row => `${row.statementNumber}-${row.isFinalized}`,
    globalFilterFn, filterFromLeafRows: true,
    filterFns: {
      dateRange: dateRangeFilter,
      relatedEntrySummary: (row, columnId, filterValue) => {
        const entries = row.getValue(columnId);
        if (!Array.isArray(entries)) return false;
        return entries.some(entry =>
          entry.toLowerCase().includes(filterValue.toLowerCase())
        );
      }
    },
    initialState: { pagination: { pageSize: 10 } },
    enableSorting: true,
    enableRowSelection: false,
    enableGlobalFilter: true,
  })

  // Initialize table data
  useEffect(() => fetchData(setData, setLoading), [])

  // Filters
  useEffect(() => {
    table.setGlobalFilter(filters.global);
  
    const correctedToggleFilters = {
      ...filters,
      isFinalized: typeof filters.isFinalized === 'boolean'
        ? !filters.isFinalized
        : filters.isFinalized
    };
  
    const tableFilterState = Object.entries(correctedToggleFilters)
      .map(([id, value]) => {
        if (id === 'relatedEntrySummary' && value) {
          return {
            id,
            value: value.trim().toLowerCase() // normalize for partial match
          };
        }
        return { id, value };
      })
      .filter(x => !(EXCLUDE_FILTERS.includes(x.id) || x.id === 'undefined'));
  
    table.setColumnFilters(tableFilterState);
  }, [table, filters]);
    
  const handleRowClick = (row) => {
    setDialogProps({
      open: true,
      statementNumber: [row.original.statementNumber, row.original.isFinalized],
      finalized: row.original.isFinalized,
      type: STATEMENT_TYPE.daily
    })
  }

  const handleNestedClick = (entryNumber, rowData) => {
    setDialogProps({
      open: true,
      statementNumber: [rowData.statementNumber, entryNumber],
      finalized: true,
      type: STATEMENT_TYPE.entry
    })
  }

  const handleRowExpansion = (row) => {
    const NestedRow = React.lazy(() => import('./NestedRow'));
    return (
      <React.Suspense fallback={<Skeleton />}>
        <NestedRow {...row} onEntrySelected={handleNestedClick} />
      </React.Suspense>
    );
  }

  const handleReset = () => {
    setFilters(RESET_FILTERS)
  }

  return <Stack spacing={2}>
    <BlankCard>
      <RenderFormSection section={filtersForm}
        formData={filters} setFormData={setFilters}
        optionsMap={optionsMap} dense spacing={1.5}
        actions={<Button onClick={handleReset}
          sx={{ minWidth: 120, px: 3, py: 1 }}
          startIcon={<FontAwesomeIcon icon={faArrowRotateLeft} />}
        >
          Reset Filters
        </Button>}
      />

    </BlankCard>
    <BlankCard>
      <Table table={table} hover
        rowCount={data.length}
        onRowClick={handleRowClick}
        renderExpandedRow={handleRowExpansion}
        onRefresh={() => fetchData(setData, setLoading)}
        loading={loading}
      />
    </BlankCard>

    <StatementDialog
      onClose={() => setDialogProps(DIALOG_PROPS_RESET)}
      {...dialogProps}
    />
  </Stack>
}